import type { ScaffolderTaskOutput } from '@backstage/plugin-scaffolder-react';
import type { GroupTone, StepLayout } from './deletionTask';
import type { AppTypeDetail } from '../shared/appTypes';
import { LEGACY_APP_TITLE } from '../shared/projectIdentity';
import {
  hasFeature,
  roleOfTemplate,
  type ProjectLifecycle,
} from '../shared/templateRoles';

/**
 * What the task page of the environment templates (`env-wake`, `env-sleep`,
 * `env-promote`, `env-configure`, those another AppType designates) reads
 * from the task: the layout of their steps,
 * and the structured report of their last step. Pure:
 * `EnvironmentTaskPage.tsx` renders it.
 */

/**
 * What the steps' texts depend on in the project's type (point 8): a
 * `standard` profile has no image of its own (its `image` steps are no-ops,
 * « sans objet : image publique du chart » in the report), a type without
 * the `backups` feature no backup to take nor restore. Odoo's, developed
 * with backups, is {@link DEVELOPED_WITH_BACKUPS}: the texts as before.
 */
export interface LayoutProfile {
  standard: boolean;
  backups: boolean;
}

export const DEVELOPED_WITH_BACKUPS: LayoutProfile = {
  standard: false,
  backups: true,
};

/**
 * The layout profile of `lifecycle` (`useProjectLifecycle`); developed with
 * backups while unknown, as an Odoo project — display only.
 */
export function layoutProfileOf(
  lifecycle: Pick<ProjectLifecycle, 'profile' | 'features'> | undefined,
): LayoutProfile {
  return {
    standard: lifecycle?.profile === 'standard',
    backups:
      lifecycle?.features === undefined || hasFeature(lifecycle, 'backups'),
  };
}

const isDefaultProfile = (profile: LayoutProfile) =>
  !profile.standard && profile.backups;

/**
 * The layout of the wake's steps (template `env-wake`, or another wake
 * template), `app` the application's name (`taskTemplate.ts`).
 */
export const wakeLayout = (
  app: string,
  profile: LayoutProfile = DEVELOPED_WITH_BACKUPS,
): StepLayout => ({
  phases: [
    // `media`: the step a wake template has when its type may keep its
    // media on S3 (`waadoo:env:wake`, stage media); absent otherwise.
    { title: 'Préparation', ids: ['check', 'media', 'activate'] },
    { title: 'Déploiement', ids: ['image', 'deploy'] },
    { title: 'Données', ids: ['data'] },
  ],
  details: {
    check: 'droits, environnement en sommeil, une tâche à la fois',
    media: 'base neuve : bucket des médias vidé avant l’activation',
    activate: '.active ajouté dans le dépôt appenv',
    image: profile.standard
      ? 'sans objet : image publique du chart'
      : 'premier pipeline si la branche n’a pas encore d’image',
    deploy: `Application ArgoCD, ${app} prêt`,
    data: profile.backups
      ? 'dernière sauvegarde restaurée, ou base neuve'
      : 'sans objet : aucune donnée à restaurer',
  },
});

/** The wake's layout of an Odoo project. */
export const WAKE_LAYOUT: StepLayout = wakeLayout(LEGACY_APP_TITLE);

/**
 * The layout of the sleep's steps (template `env-sleep`) of a type with
 * backups.
 */
export const SLEEP_LAYOUT: StepLayout = {
  phases: [
    { title: 'Préparation', ids: ['check'] },
    { title: 'Sauvegarde', ids: ['backup'] },
    { title: 'Arrêt', ids: ['stop'] },
  ],
  details: {
    check: 'droits, environnement actif, aucune sauvegarde en cours',
    backup: 'sauvegarde finale, vérifiée dans le bucket',
    stop: '.active retiré, environnement supprimé',
  },
};

/**
 * The sleep's layout of a type without backups (point 8): allowed only for
 * an environment without data (backend `sleep.ts`), its `backup` step a
 * no-op, folded into « Préparation ».
 */
export const SLEEP_LAYOUT_NO_BACKUPS: StepLayout = {
  phases: [
    { title: 'Préparation', ids: ['check', 'backup'] },
    { title: 'Arrêt', ids: ['stop'] },
  ],
  details: {
    check: 'droits, environnement actif, sans données (ni base ni volume)',
    backup: 'sans objet : aucune donnée à sauvegarder',
    stop: '.active retiré, environnement supprimé',
  },
};

/**
 * The layout of the promotion's steps (template `env-promote`, or another
 * promotion template): its four phases, as the user asked (Préparation, Merge
 * request, Image, Déploiement). For a `standard` profile, three: its `image`
 * step a no-op, folded into « Déploiement », and the source checked
 * deployed at its head (backend `promote.ts`).
 */
export const promoteLayout = (
  app: string,
  profile: LayoutProfile = DEVELOPED_WITH_BACKUPS,
): StepLayout =>
  profile.standard
    ? {
        phases: [
          { title: 'Préparation', ids: ['check'] },
          { title: 'Merge request', ids: ['merge'] },
          { title: 'Déploiement', ids: ['image', 'deploy'] },
        ],
        details: {
          check:
            'droits, branche suivante, source déployée à sa dernière révision, une tâche à la fois',
          merge: 'merge request ouverte (ou reprise), puis fusionnée',
          image: 'sans objet : image publique du chart',
          deploy: `environnement redéployé à la révision fusionnée, ${app} prêt ; rien s’il est en sommeil`,
        },
      }
    : {
        phases: [
          { title: 'Préparation', ids: ['check'] },
          { title: 'Merge request', ids: ['merge'] },
          { title: 'Image', ids: ['image'] },
          { title: 'Déploiement', ids: ['deploy'] },
        ],
        details: {
          check:
            'droits, branche suivante, commits à promouvoir, une tâche à la fois',
          merge: 'merge request ouverte (ou reprise), puis fusionnée',
          image:
            'pipeline de la branche cible : image de la source reprise, jamais reconstruite',
          deploy: `environnement redéployé, ${app} prêt ; rien s’il est en sommeil`,
        },
      };

/** The promotion's layout of an Odoo project. */
export const PROMOTE_LAYOUT: StepLayout = promoteLayout(LEGACY_APP_TITLE);

/**
 * The layout of a change of configuration's steps (template
 * `env-configure`, or another configuration template).
 */
export const configureLayout = (app: string): StepLayout => ({
  phases: [
    { title: 'Préparation', ids: ['check'] },
    { title: 'Commit', ids: ['commit'] },
    { title: 'Déploiement', ids: ['deploy'] },
  ],
  details: {
    check: 'droits, valeurs dans les bornes, une tâche à la fois',
    commit: 'values.yaml modifié dans le dépôt appenv, commentaires conservés',
    deploy: `environnement redéployé, ${app} prêt, volumes agrandis ; rien s’il est en sommeil`,
  },
});

/** The change of configuration's layout of an Odoo project. */
export const CONFIGURE_LAYOUT: StepLayout = configureLayout(LEGACY_APP_TITLE);

/**
 * The step ids of each operation of the `restore` template (`env-restore`,
 * platform-catalog `scaffolder/env-restore/template.yaml`): one step per
 * stage of `waadoo:env:restore`, each conditional on the task's
 * `operation`, so a task's spec lists the three sets and runs one.
 */
const RESTORE_STEPS = [
  'restore-check',
  'restore-prepare',
  'restore-stop',
  'restore-rewind',
  'restore-commit',
  'restore-wait',
  'restore-start',
  'restore-backup',
];
const VALIDATE_STEPS = ['validate-check', 'validate-commit', 'validate-wait'];
const CANCEL_STEPS = [
  'cancel-check',
  'cancel-stop',
  'cancel-rewind',
  'cancel-commit',
  'cancel-wait',
  'cancel-start',
];

/** The point-in-time restore's layout (operation `restore`). */
export const RESTORE_LAYOUT: StepLayout = {
  phases: [
    { title: 'Préparation', ids: ['restore-check', 'restore-prepare'] },
    { title: 'Arrêt et retour', ids: ['restore-stop', 'restore-rewind'] },
    { title: 'Bascule', ids: ['restore-commit', 'restore-wait'] },
    { title: 'Redémarrage', ids: ['restore-start', 'restore-backup'] },
  ],
  details: {
    'restore-check':
      'droits, instant dans la fenêtre, aucune restauration en cours, place dans le quota',
    'restore-prepare': 'journaux archivés au-delà de l’instant choisi',
    'restore-stop': 'application arrêtée avant le retour des médias',
    'restore-rewind':
      'buckets de données ramenés à l’instant + 60 s ; rien sans bucket',
    'restore-commit':
      'nouvelle génération de la base dans le dépôt appenv, références suivies',
    'restore-wait': 'Sync ArgoCD, nouvelle base saine',
    'restore-start': 'application redémarrée',
    'restore-backup': 'première sauvegarde de la nouvelle base',
  },
  omit: [...VALIDATE_STEPS, ...CANCEL_STEPS],
};

/** The validation of a restore (operation `validate`). */
export const RESTORE_VALIDATE_LAYOUT: StepLayout = {
  phases: [
    { title: 'Préparation', ids: ['validate-check'] },
    { title: 'Validation', ids: ['validate-commit', 'validate-wait'] },
  ],
  details: {
    'validate-check':
      'droits, restauration à valider, nouvelle base sauvegardée, application en marche',
    'validate-commit': 'restauration retirée du dépôt appenv',
    'validate-wait': 'Sync ArgoCD, ancienne base supprimée',
  },
  omit: [...RESTORE_STEPS, ...CANCEL_STEPS],
};

/** The cancellation of a restore (operation `cancel`). */
export const RESTORE_CANCEL_LAYOUT: StepLayout = {
  phases: [
    { title: 'Préparation', ids: ['cancel-check'] },
    { title: 'Arrêt et retour', ids: ['cancel-stop', 'cancel-rewind'] },
    { title: 'Bascule', ids: ['cancel-commit', 'cancel-wait'] },
    { title: 'Redémarrage', ids: ['cancel-start'] },
  ],
  details: {
    'cancel-check': 'droits, restauration à annuler',
    'cancel-stop': 'application arrêtée',
    'cancel-rewind': 'buckets de données ramenés à l’arrêt de la restauration',
    'cancel-commit': 'retour à l’ancienne génération dans le dépôt appenv',
    'cancel-wait': 'Sync ArgoCD, ancienne base en service',
    'cancel-start': 'application redémarrée',
  },
  omit: [...RESTORE_STEPS, ...VALIDATE_STEPS],
};

/** Mirror of `EnvReportData` (backend `environmentPower/report.ts`). */
export interface EnvReportRow {
  element: string;
  detail: string;
  action: string;
  outcome: 'removed' | 'changed' | 'noop' | 'error';
  kind: 'removed' | 'emptied' | 'changed' | 'noop' | 'error';
  items?: string[];
  icon?: string;
}

export interface EnvReportData {
  operation:
    | 'wake'
    | 'sleep'
    | 'promote'
    | 'configure'
    | 'restore'
    | 'restore-validate'
    | 'restore-cancel';
  project: string;
  /**
   * The environment woken, put to sleep, promoted to, configured, or
   * restored.
   */
  environment: string;
  /** A promotion's source branch. */
  source?: string;
  /** A promotion's merge request. */
  mergeRequestUrl?: string;
  outcome: 'ok' | 'partial';
  rows: EnvReportRow[];
  notes: string[];
  warnings: string[];
  url?: string;
}

/** The output key the templates give the last step's `reportData` under. */
export const ENV_REPORT_OUTPUT_KEY = 'environmentReport';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === 'string');
const oneOf = (value: unknown, ...values: string[]) =>
  typeof value === 'string' && values.includes(value);

function isRow(value: unknown): value is EnvReportRow {
  return (
    isRecord(value) &&
    typeof value.element === 'string' &&
    typeof value.detail === 'string' &&
    typeof value.action === 'string' &&
    oneOf(value.outcome, 'removed', 'changed', 'noop', 'error') &&
    oneOf(value.kind, 'removed', 'emptied', 'changed', 'noop', 'error') &&
    (value.items === undefined || isStringArray(value.items)) &&
    (value.icon === undefined || typeof value.icon === 'string')
  );
}

/**
 * The structured report of a finished task, or undefined when the output
 * has none or one of another shape — the page then shows the markdown
 * outputs.
 */
export function readEnvReport(
  output: ScaffolderTaskOutput | undefined,
): EnvReportData | undefined {
  const value = output?.[ENV_REPORT_OUTPUT_KEY];
  if (
    !isRecord(value) ||
    !oneOf(
      value.operation,
      'wake',
      'sleep',
      'promote',
      'configure',
      'restore',
      'restore-validate',
      'restore-cancel',
    ) ||
    typeof value.project !== 'string' ||
    typeof value.environment !== 'string' ||
    (value.source !== undefined && typeof value.source !== 'string') ||
    (value.mergeRequestUrl !== undefined &&
      typeof value.mergeRequestUrl !== 'string') ||
    !oneOf(value.outcome, 'ok', 'partial') ||
    !Array.isArray(value.rows) ||
    !value.rows.every(isRow) ||
    !isStringArray(value.notes) ||
    !isStringArray(value.warnings) ||
    (value.url !== undefined && typeof value.url !== 'string')
  ) {
    return undefined;
  }
  return value as unknown as EnvReportData;
}

/** One group of the report: its pill, its rows. */
export interface EnvRowGroup {
  kind: EnvReportRow['kind'];
  title: string;
  tone: GroupTone;
  rows: Array<{ element: string; icon?: string; items: string[] }>;
}

const GROUPS: ReadonlyArray<{
  kind: EnvReportRow['kind'];
  tone: GroupTone;
  title: string;
}> = [
  { kind: 'error', tone: 'danger', title: 'En erreur' },
  { kind: 'changed', tone: 'info', title: 'Fait' },
  { kind: 'removed', tone: 'warning', title: 'Arrêté et supprimé' },
  { kind: 'noop', tone: 'success', title: 'Conservé, déjà fait ou sans objet' },
];

/** The report's rows by kind, the empty groups left out. */
export function envRowGroups(report: EnvReportData): EnvRowGroup[] {
  return GROUPS.map(group => ({
    kind: group.kind,
    title: group.title,
    tone: group.tone,
    rows: report.rows
      .filter(row => row.kind === group.kind)
      .map(row => ({
        element: row.element,
        ...(row.icon !== undefined ? { icon: row.icon } : {}),
        items: row.items ?? [row.detail],
      })),
  })).filter(group => group.rows.length > 0);
}

/** The result line of the report. */
export function envResultTitle(report: EnvReportData): string {
  let what = `${report.environment} est en sommeil`;
  if (report.operation === 'wake') {
    what = `${report.environment} est réveillé`;
  } else if (report.operation === 'promote') {
    what = `${report.source ?? '?'} est promu vers ${report.environment}`;
  } else if (report.operation === 'configure') {
    what = `La configuration de ${report.environment} est appliquée`;
  } else if (report.operation === 'restore') {
    what = `${report.environment} est restauré à l’instant choisi, à valider`;
  } else if (report.operation === 'restore-validate') {
    what = `La restauration de ${report.environment} est validée`;
  } else if (report.operation === 'restore-cancel') {
    what = `La restauration de ${report.environment} est annulée`;
  }
  return report.outcome === 'ok' ? what : `${what}, avec des points à vérifier`;
}

/**
 * The layout of `make` for `app` and `profile`: the legacy type's (Odoo's)
 * the constant itself for the default profile.
 */
function layoutFor(
  app: string,
  profile: LayoutProfile,
  make: (name: string, profile: LayoutProfile) => StepLayout,
  legacy: StepLayout,
): StepLayout {
  return app === LEGACY_APP_TITLE && isDefaultProfile(profile)
    ? legacy
    : make(app, profile);
}

/**
 * The layout and the fallback title of a task of this page, by the role of
 * its template (`roleOfTemplate`, with the descriptors `appTypes` for a
 * template another type designates): the templates of a role share their
 * step ids, so their layout, which names the application `appTitle`
 * (`taskTemplate.ts`); the title does not name the application. Anything
 * else as a sleep, as before. The texts follow the project's `lifecycle`
 * (`layoutProfileOf`): Odoo's while unknown.
 */
export function pageOf(
  templateRef: string | undefined,
  options: {
    appTypes?: readonly AppTypeDetail[];
    appTitle?: string;
    lifecycle?: Pick<ProjectLifecycle, 'profile' | 'features'>;
    /** The task's `operation` parameter: a restore's layout follows it. */
    operation?: unknown;
  } = {},
) {
  const app = options.appTitle ?? LEGACY_APP_TITLE;
  const profile = layoutProfileOf(options.lifecycle);
  const role = roleOfTemplate(templateRef, options.appTypes);
  if (role === 'wake') {
    return {
      layout: layoutFor(app, profile, wakeLayout, WAKE_LAYOUT),
      title: 'Réveiller un environnement',
    };
  }
  if (role === 'promote') {
    return {
      layout: layoutFor(app, profile, promoteLayout, PROMOTE_LAYOUT),
      title: 'Promouvoir une branche permanente',
    };
  }
  if (role === 'configure') {
    return {
      layout: layoutFor(app, profile, configureLayout, CONFIGURE_LAYOUT),
      title: 'Modifier la configuration d’un environnement',
    };
  }
  if (role === 'restore') {
    if (options.operation === 'validate') {
      return {
        layout: RESTORE_VALIDATE_LAYOUT,
        title: 'Valider la restauration d’un environnement',
      };
    }
    if (options.operation === 'cancel') {
      return {
        layout: RESTORE_CANCEL_LAYOUT,
        title: 'Annuler la restauration d’un environnement',
      };
    }
    return {
      layout: RESTORE_LAYOUT,
      title: 'Restaurer un environnement à un instant',
    };
  }
  return {
    layout: profile.backups ? SLEEP_LAYOUT : SLEEP_LAYOUT_NO_BACKUPS,
    title: 'Mettre en sommeil un environnement',
  };
}
